// FE-SCRM-050 — Prospect detail REST client.
//
// Hits the BE-SCRM-003 prospect endpoints, the BE-SCRM-005 contact
// list, the BE-SCRM-004 opportunity list (filtered by ``prospect_id``),
// the DATA-SCRM-019 alert list (also filtered by ``prospect_id``), and
// the new ``/benefits-intelligence`` endpoint introduced for this
// chunk. Tag suggestions ride the FE-SCRM-060 endpoint.

import { api } from "@/api/client";

import type { BenefitsIntelligence } from "../BenefitsIntelligence";
import type { TagSuggestion } from "../components";

import type {
  Form5500SponsorSearchResponse,
  Form5500SponsorSuggestion,
  ProspectAlertListResponse,
  ProspectDetail,
  ProspectOpportunityListResponse,
  SalesContactCreateInput,
  SalesContactListResponse,
  SalesContactRow,
} from "./types";


export function fetchProspectDetail(
  prospect_id: string,
  opts?: { signal?: AbortSignal },
): Promise<ProspectDetail> {
  return api.get<ProspectDetail>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}`,
    opts,
  );
}


export function patchProspect(
  prospect_id: string,
  body: Partial<ProspectDetail> & { tags?: string[] },
): Promise<ProspectDetail> {
  return api.patch<ProspectDetail>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}`,
    body,
  );
}


export function archiveProspect(
  prospect_id: string,
): Promise<ProspectDetail> {
  return api.post<ProspectDetail>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}/archive`,
  );
}


export function fetchProspectBenefitsIntelligence(
  prospect_id: string,
  opts?: { signal?: AbortSignal },
): Promise<BenefitsIntelligence> {
  return api.get<BenefitsIntelligence>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}`
    + `/benefits-intelligence`,
    opts,
  );
}


export function fetchProspectContacts(
  prospect_id: string,
  opts?: { signal?: AbortSignal },
): Promise<SalesContactListResponse> {
  return api.get<SalesContactListResponse>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}/contacts`,
    opts,
  );
}


export function createProspectContact(
  prospect_id: string,
  input: SalesContactCreateInput,
): Promise<SalesContactRow> {
  return api.post<SalesContactRow>(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}/contacts`,
    input,
  );
}


export function fetchProspectOpportunities(
  prospect_id: string,
  opts?: { signal?: AbortSignal },
): Promise<ProspectOpportunityListResponse> {
  const qs = new URLSearchParams({ prospect_id });
  return api.get<ProspectOpportunityListResponse>(
    `/api/v1/sales/opportunities?${qs.toString()}`,
    opts,
  );
}


export function fetchProspectAlerts(
  prospect_id: string,
  opts?: { signal?: AbortSignal },
): Promise<ProspectAlertListResponse> {
  const qs = new URLSearchParams({ prospect_id });
  return api.get<ProspectAlertListResponse>(
    `/api/v1/sales/prospect-alerts?${qs.toString()}`,
    opts,
  );
}


export function acknowledgeProspectAlert(
  alert_id: string,
): Promise<unknown> {
  return api.post(
    `/api/v1/sales/prospect-alerts/${encodeURIComponent(alert_id)}`
    + `/acknowledge`,
  );
}


// FE-SCRM-062 — Form 5500 manual link / unlink + sponsor typeahead.
// The sponsor search endpoint lives outside the /sales/ prefix
// because Form 5500 reference data is global; the link/unlink path
// is on the existing tenant-scoped Prospect surface.

export function searchForm5500Sponsors(
  q: string,
  opts?: { limit?: number; signal?: AbortSignal },
): Promise<Form5500SponsorSuggestion[]> {
  const qs = new URLSearchParams({ q });
  if (opts?.limit) qs.set("limit", String(opts.limit));
  return api
    .get<Form5500SponsorSearchResponse>(
      `/api/v1/form5500/sponsors?${qs.toString()}`,
      opts?.signal ? { signal: opts.signal } : undefined,
    )
    .then((body) => body.rows ?? []);
}


export function linkProspectToForm5500Sponsor(
  prospect_id: string, sponsor_ein: string,
): Promise<unknown> {
  return api.post(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}`
    + `/link-form5500`,
    { sponsor_ein },
  );
}


export function unlinkProspectFromForm5500Sponsor(
  prospect_id: string,
): Promise<unknown> {
  return api.post(
    `/api/v1/sales/prospects/${encodeURIComponent(prospect_id)}`
    + `/unlink-form5500`,
  );
}


// FE-SCRM-060 tag autocomplete wrapper — bound to ``entity_type=prospect``
// so the inline TagsInput on this page surfaces usage counts from the
// shared AccountTag inventory.
interface TagSuggestionResponse {
  tags: TagSuggestion[];
}


export function fetchProspectTagSuggestions(
  q: string,
): Promise<TagSuggestion[]> {
  const qs = new URLSearchParams({ q, entity_type: "prospect" });
  return api
    .get<TagSuggestionResponse>(`/api/v1/sales/tags/suggestions?${qs.toString()}`)
    .then((body) => body.tags ?? []);
}
